import React, { useMemo, useRef, useState, useEffect } from "react";
import { getAllStudents } from "../../services/studentsService";
import { matchLevelConfig, getPaymentModeConfig, computeTotalDue, computeStudentFinance, parseMoneyAmount } from "../../utils/feeCalculator";
import "./TuitionLedger.css";
import { getCurrentUser } from "../../auth";
import { USE_LOCAL_FALLBACK } from "../../config/firebaseConfig";
import { db } from "../../services/firebase";
import { collection, getDocs } from "firebase/firestore";

const MONTHS = ["Jun","Jul","Aug","Sep","Oct","Nov","Dec","Jan","Feb","Mar"];

function sum(arr) {
  return arr.reduce((a, b) => a + Number(b || 0), 0);
}

function monthKey(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const m = d.getMonth(); // 0=Jan
  // School year Jun..Mar (JS months: 0=Jan)
  const map = {5:"Jun",6:"Jul",7:"Aug",8:"Sep",9:"Oct",10:"Nov",11:"Dec",0:"Jan",1:"Feb",2:"Mar"};
  return map[m] || "";
}

export default function TuitionLedger() {
  const currentUser = getCurrentUser();
  const userCampus = currentUser?.campus;
  const isAdmin = currentUser?.role === "admin" || currentUser?.role === "superadmin" || userCampus === "all";
  const [campus, setCampus] = useState(() => (isAdmin ? "" : (userCampus || "")));
  const [sectionFilter, setSectionFilter] = useState("");
  const [modeFilter, setModeFilter] = useState("");
  const printRef = useRef(null);

  const [payments, setPayments] = useState([]);
  useEffect(() => {
    (async () => {
      if (!USE_LOCAL_FALLBACK) {
        try {
          const snap = await getDocs(collection(db, "payments"));
          const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
          setPayments(rows);
        } catch {
          /* keep local */
        }
      }
    })();
  }, []);

  const paymentModesMap = useMemo(() => {
    try {
      const raw = localStorage.getItem("studentPaymentModes");
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }, []);

  const allStudents = useMemo(() => getAllStudents(), []);

  const sections = useMemo(() => {
    const set = new Set();
    allStudents.forEach(s => {
      const sec = s.section ? s.section : (s.strand || "");
      if (sec) set.add(sec);
    });
    return ["", ...Array.from(set)];
  }, [allStudents]);

  const campusOptions = useMemo(() => {
    const set = new Set(allStudents.map(s => s.campus).filter(Boolean));
    return ["", ...Array.from(set)];
  }, [allStudents]);

  const rows = useMemo(() => {
    const base = allStudents.filter(s => {
      if (!isAdmin && userCampus && s.campus !== userCampus) return false;
      if (campus && s.campus !== campus) return false;
      if (sectionFilter) {
        const sec = s.section ? s.section : (s.strand || "");
        if (sec !== sectionFilter) return false;
      }
      if (modeFilter && (paymentModesMap[s.id] || s.paymentMode || "Annual") !== modeFilter) return false;
      return true;
    });
    return base.map(s => {
      const mode = paymentModesMap[s.id] || s.paymentMode || "Annual";
      const studentPayments = payments.filter(p => p.studentId === s.id);
      // Schedule + total due
      let schedule = [];
      let totalDue = 0;
      // Always compute from configs as source of truth
      const finCfg = computeStudentFinance({ ...s, paymentMode: mode }, studentPayments, "Annual");
      const recomputedSchedule = (finCfg.schedule || []).map(ent => ({
        label: ent.label || ent.period || "",
        misc: Number(ent.misc || 0),
        tuition: Number(ent.tuition || 0),
        total: Number(ent.total || (Number(ent.misc || 0) + Number(ent.tuition || 0))),
      }));
      const recomputedTotal = Number(finCfg.totalDue || computeTotalDue(getPaymentModeConfig(matchLevelConfig(s.campus, s.gradeLevel), mode)) || 0);
      // Use saved only if it matches recomputed total (prevents double-discounted totals)
      if (s.paymentSchedule?.schedule) {
        const savedSchedule = s.paymentSchedule.schedule.map(ent => ({
          label: ent.label || ent.period || "",
          misc: Number(ent.misc || 0),
          tuition: Number(ent.tuition || 0),
          total: Number(ent.total || (Number(ent.misc || 0) + Number(ent.tuition || 0))),
        }));
        const savedTotal = Number(s.paymentSchedule.totalDue || savedSchedule.reduce((t,e)=>t+Number(e.total||0),0));
        if (Math.abs(savedTotal - recomputedTotal) < 0.5) {
          schedule = savedSchedule;
          totalDue = savedTotal;
        } else {
          schedule = recomputedSchedule;
          totalDue = recomputedTotal;
        }
      } else {
        schedule = recomputedSchedule;
        totalDue = recomputedTotal;
      }
      const totalPaid = studentPayments.reduce((t,p)=>t+parseMoneyAmount(p.amount),0);
      const balance = Math.max(0, totalDue - totalPaid);
      const methodsUsed = Array.from(new Set(studentPayments.map(p => (p.method || "").trim()).filter(Boolean))).join(", ");
      const latestMethod = (() => {
        if (studentPayments.length === 0) return "";
        const sorted = studentPayments.slice().sort((a,b) => new Date(b.date) - new Date(a.date));
        return sorted[0]?.method || "";
      })();

      // Classify payments by month into Tuition vs Misc
      const accountsMap = (() => {
        try {
          const raw = localStorage.getItem("accounts");
          const list = raw ? JSON.parse(raw) : [];
          const map = {};
          (list || []).forEach(a => { map[a.id] = a; });
          return map;
        } catch {
          return {};
        }
      })();
      const monthTuition = {}; const monthMisc = {};
      MONTHS.forEach(m => { monthTuition[m] = 0; monthMisc[m] = 0; });
      const tuitionOrsByMonth = {};
      MONTHS.forEach(m => { tuitionOrsByMonth[m] = new Set(); });
      const miscOrs = new Set();
      // Derive misc dues from schedule before allocating payments
      const miscTotalDue = (schedule || []).reduce((sum, e) => sum + Number(e.misc || 0), 0);
      const misc1Due = Number(miscTotalDue || 0);
      const misc2Due = 0;
      let miscPaidTotal = 0;
      const modeKey = String(mode || "").toLowerCase();
      const isInstallment = modeKey.includes("install");
      const isSemestral = modeKey.includes("sem");
      const isAnnual = modeKey.includes("annual") || (!isInstallment && !isSemestral);

      const tuitionBuckets = [];
      const dueMonthly = {};
      if (isInstallment) {
        const monthsSeq = ["Jun","Jul","Aug","Sep","Oct","Nov","Dec","Jan","Feb","Mar"];
        let seqIdx = 0;
        (schedule || []).forEach(e => {
          const tuitionAmt = Number(e.tuition || 0);
          if (tuitionAmt <= 0) return;
          const rawLbl = String(e.label || e.period || "").slice(0,3);
          const month = MONTHS.includes(rawLbl) ? rawLbl : (monthsSeq[seqIdx] || monthsSeq[monthsSeq.length - 1]);
          tuitionBuckets.push({ month, left: tuitionAmt });
          dueMonthly[month] = (dueMonthly[month] || 0) + tuitionAmt;
          seqIdx = Math.min(seqIdx + 1, monthsSeq.length - 1);
        });
      } else if (isSemestral) {
        const tuitionLines = (schedule || []).filter(e => Number(e.tuition || 0) > 0);
        const first = Number(tuitionLines[0]?.tuition || 0);
        const second = Number(tuitionLines[1]?.tuition || 0);
        if (first > 0) {
          tuitionBuckets.push({ month: "Jun", left: first });
          dueMonthly["Jun"] = first;
        }
        if (second > 0) {
          tuitionBuckets.push({ month: "Oct", left: second });
          dueMonthly["Oct"] = second;
        }
      } else if (isAnnual) {
        const tuitionDue = (schedule || []).reduce((sum, e) => sum + Number(e.tuition || 0), 0);
        if (tuitionDue > 0) {
          tuitionBuckets.push({ month: "Jun", left: tuitionDue });
          dueMonthly["Jun"] = tuitionDue;
        }
      }

      const orderedPayments = studentPayments.slice().sort((a, b) => new Date(a.date) - new Date(b.date));
      let miscRemaining = misc1Due;
      let cursor = 0;
      orderedPayments.forEach(p => {
        let amt = parseMoneyAmount(p.amount);
        const scope = String(p.applyScope || "both").toLowerCase();
        const miscOnly = scope === "misc";
        const targetPeriod = String(p.applyPeriod || "").slice(0, 3);
        const orNo = String(p.reference || "").trim();

        if (miscRemaining > 0 && amt > 0) {
          const alloc = Math.min(amt, miscRemaining);
          miscPaidTotal += alloc;
          miscRemaining -= alloc;
          amt -= alloc;
          if (alloc > 0 && orNo) miscOrs.add(orNo);
        }
        if (miscOnly || amt <= 0) return;

        let idx = cursor;
        if (targetPeriod) {
          const found = tuitionBuckets.findIndex(b => b.month === targetPeriod);
          if (found < 0) return;
          idx = found;
        }

        while (amt > 0 && idx < tuitionBuckets.length) {
          const b = tuitionBuckets[idx];
          if (!b) break;
          if (b.left > 0) {
            const alloc = Math.min(amt, b.left);
            monthTuition[b.month] += alloc;
            b.left -= alloc;
            amt -= alloc;
            if (alloc > 0 && orNo && tuitionOrsByMonth[b.month]) tuitionOrsByMonth[b.month].add(orNo);
          }
          if (targetPeriod) break;
          if (b.left <= 0) {
            idx += 1;
            cursor = Math.max(cursor, idx);
          } else {
            break;
          }
        }

        if (amt > 0 && !targetPeriod && tuitionBuckets.length > 0) {
          const last = tuitionBuckets[tuitionBuckets.length - 1];
          monthTuition[last.month] += amt;
          if (amt > 0 && orNo && tuitionOrsByMonth[last.month]) tuitionOrsByMonth[last.month].add(orNo);
        }
      });
      monthTuition.__dueMonthly = dueMonthly;
      const sem1Months = ["Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
      const sem2Months = ["Jan","Feb","Mar"];
      const sem1 = sum(sem1Months.map(m => monthTuition[m] || 0));
      const sem2 = sum(sem2Months.map(m => monthTuition[m] || 0));
      const annual = sem1 + sem2;
      let misc1Paid = Number(miscPaidTotal || 0);
      let misc2Paid = 0;
      const paidMonthsCount = (() => {
        if (isInstallment) {
          const dueMap = monthTuition.__dueMonthly || {};
          let count = 0;
          for (const m of MONTHS) {
            const due = Number(dueMap[m] || 0);
            if (due <= 0) continue;
            if (Number(monthTuition[m] || 0) >= due) count += 1;
          }
          return Math.min(10, count);
        }
        if (isSemestral) {
          const dueMap = monthTuition.__dueMonthly || {};
          let count = 0;
          const dueJun = Number(dueMap["Jun"] || 0);
          const dueOct = Number(dueMap["Oct"] || 0);
          if (dueJun > 0 && Number(monthTuition["Jun"] || 0) >= dueJun) count += 1;
          if (dueOct > 0 && Number(monthTuition["Oct"] || 0) >= dueOct) count += 1;
          return Math.min(2, count);
        }
        if (isAnnual) {
          const tuitionDue = (schedule || []).reduce((sum, e) => sum + Number(e.tuition || 0), 0);
          const miscDue = (schedule || []).reduce((sum, e) => sum + Number(e.misc || 0), 0);
          const totalDueAll = tuitionDue + miscDue;
          const paidAll = totalPaid;
          if (paidAll < totalDueAll) return 0;
          const baseYear = Number(s?.enrollmentYear);
          const cutoffYear = Number.isFinite(baseYear) && baseYear > 1900 ? baseYear : new Date().getFullYear();
          const cutoff = new Date(cutoffYear, 5, 30, 23, 59, 59);
          const lastPaymentDate = studentPayments.reduce((max, p) => {
            const d = new Date(p.date);
            return Number.isFinite(d.getTime()) && d > max ? d : max;
          }, new Date(0));
          return lastPaymentDate <= cutoff ? 1 : 0;
        }
        return MONTHS.filter(m => Number(monthTuition[m] || 0) > 0).length;
      })();
      const scheduleMonthTuition = new Set(
        (schedule || [])
          .filter(e => Number(e.tuition || 0) > 0)
          .map(e => String(e.label || e.period || "").slice(0,3))
          .filter(k => MONTHS.includes(k))
      );
      const requiredMonths = isInstallment ? 10 : (isSemestral ? 2 : 1);
      const pendingMonthsCount = Math.max(0, requiredMonths - paidMonthsCount);


      return {
        id: s.id,
        name: s.fullname || `${s.firstName || ""} ${s.lastName || ""}`.trim(),
        gender: s.gender || "",
        campus: s.campus || "",
        section: s.section || s.strand || "",
        mode,
        latestMethod,
        methodsUsed,
        monthsTuition: monthTuition,
        monthsMisc: monthMisc,
        tuitionOrsByMonth: Object.fromEntries(MONTHS.map(m => [m, Array.from(tuitionOrsByMonth[m] || [])])),
        miscOrs: Array.from(miscOrs),
        annual, sem1, sem2,
        misc1Due, misc2Due,
        misc1Paid, misc2Paid,
        paidMonthsCount, requiredMonths,
        totalPaid,
        balance,
        totalTuition: totalDue,
        dueMonthly: monthTuition.__dueMonthly || {}
      };
    });
  }, [allStudents, payments, campus, sectionFilter, modeFilter, paymentModesMap]);

  function handlePrint() {
    const content = printRef.current ? printRef.current.outerHTML : "";
    if (!content) return;
    // Use an offscreen iframe to avoid popup blockers
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    document.body.appendChild(iframe);
    const doc = iframe.contentWindow ? iframe.contentWindow.document : iframe.document;
    doc.open();
    doc.write(`<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Tuition Ledger</title>
    <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css" />
    <style>
      @page { size: A4 landscape; margin: 10mm; }
      .ledger-table { font-size: 10px; }
      .ledger-table th, .ledger-table td { padding: 4px 6px; vertical-align: middle; }
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    </style>
  </head>
  <body>
    <div style="font-size:12px;margin-bottom:6px;">
      <strong>Filters:</strong> ${campus || "All Campuses"} • ${sectionFilter || "All Sections"} • ${modeFilter || "All Modes"}
    </div>
    ${content}
  </body>
</html>`);
    doc.close();
    const win = iframe.contentWindow;
    // Delay to allow layout and fonts
    setTimeout(() => {
      win.focus();
      win.print();
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 200);
    }, 250);
  }

  return (
    <div style={{ padding: 24 }}>
      <div className="no-print d-flex justify-content-between align-items-center mb-2">
        <div className="d-flex align-items-center gap-3">
          <h3 className="m-0">Tuition Fee Payment Ledger</h3>
          <a className="btn btn-sm btn-outline-secondary" href="/finance/settings/chart-of-accounts" target="_blank" rel="noreferrer">Accounts & mappings</a>
          {isAdmin && (
            <div className="d-flex align-items-center gap-2">
              <label className="form-label mb-0">Campus</label>
              <select className="form-select" value={campus} onChange={e => setCampus(e.target.value)} style={{ width: 160 }}>
                <option value="">All</option>
                <option value="gapan">gapan</option>
                <option value="bulacan">bulacan</option>
              </select>
            </div>
          )}
        </div>
        <div className="d-flex gap-2">
          <input
            type="file"
            accept=".csv,application/json"
            className="form-control form-control-sm"
            onChange={(e) => {
              const file = e.target.files && e.target.files[0];
              if (!file) return;
              const reader = new FileReader();
              reader.onload = () => {
                try {
                  const text = String(reader.result || "");
                  let items = [];
                  if (file.name.toLowerCase().endsWith(".json")) {
                    const parsed = JSON.parse(text);
                    items = Array.isArray(parsed) ? parsed : [];
                  } else {
                    const lines = text.split(/\\r?\\n/).filter(Boolean);
                    if (lines.length > 0) {
                      const header = lines[0].split(",").map(h => h.trim());
                      for (let i = 1; i < lines.length; i++) {
                        const cols = lines[i].split(",").map(c => c.trim());
                        const obj = {};
                        header.forEach((h, idx) => { obj[h] = cols[idx] || ""; });
                        items.push(obj);
                      }
                    }
                  }
                  const normalized = items.map((r, idx) => ({
                    id: r.id || `SUBJ-${Date.now()}-${idx}`,
                    code: r.code || r.Code || "",
                    name: r.name || r.Name || "",
                    gradeLevel: r.gradeLevel || r["Grade Level"] || "",
                    strand: r.strand || r.Strand || "",
                    teacher: r.teacher || r.Teacher || "",
                    schedule: r.schedule || r.Schedule || "",
                    room: r.room || r.Room || "",
                  })).filter(it => it.code && it.name);
                  if (normalized.length === 0) {
                    alert("No valid subjects found in file.");
                    return;
                  }
                  const prevRaw = localStorage.getItem("subjects");
                  const prev = prevRaw ? JSON.parse(prevRaw) : [];
                  const codes = new Set(prev.map(p => p.code));
                  const merged = [...prev];
                  normalized.forEach(n => { if (!codes.has(n.code)) merged.push(n); });
                  localStorage.setItem("subjects", JSON.stringify(merged));
                  alert(`Imported ${normalized.length} subject(s). They are now available in SIS > Subjects and Enrollment.`);
                } catch {
                  alert("Failed to import file.");
                } finally {
                  e.target.value = "";
                }
              };
              reader.readAsText(file);
            }}
          />
          <button className="btn btn-primary" onClick={handlePrint}>Print</button>
        </div>
      </div>
      <div className="small text-muted mb-2">{`Selected: ${campus || "All Campuses"} • ${sectionFilter || "All Sections"} • ${modeFilter || "All Modes"}`}</div>
      <div className="row g-2 mb-3 no-print">
        <div className="col-md-3">
          <label className="form-label">Campus</label>
          <select className="form-select" value={campus} onChange={e=>setCampus(e.target.value)} disabled={!isAdmin}>
            {campusOptions.map(c => <option key={c} value={c}>{c || "All"}</option>)}
          </select>
        </div>
        <div className="col-md-4">
          <label className="form-label">Section/Strand</label>
          <select className="form-select" value={sectionFilter} onChange={e=>setSectionFilter(e.target.value)}>
            {sections.map(s => <option key={s} value={s}>{s || "All"}</option>)}
          </select>
        </div>
        <div className="col-md-3">
          <label className="form-label">Mode of Payment</label>
          <select className="form-select" value={modeFilter} onChange={e=>setModeFilter(e.target.value)}>
            <option value="">All</option>
            <option>Annual</option>
            <option>Semestral</option>
            <option>Installment</option>
          </select>
        </div>
      </div>
      <div style={{ overflowX: "auto" }} ref={printRef}>
        <div className="legend">
          <span className="chip"><span className="dot dot-annual"></span> Annual</span>
          <span className="chip"><span className="dot dot-semestral"></span> Semestral</span>
          <span className="chip"><span className="dot dot-installment"></span> Installment</span>
        </div>
        <table className="table table-bordered table-sm ledger-table">
          <thead>
            <tr style={{ background: "#f7fbff" }}>
              <th style={{ minWidth: 180, color: "#b71c1c" }}>Full Name</th>
              <th style={{ color: "#b71c1c" }}>Gender</th>
              <th style={{ color: "#b71c1c" }}>Section</th>
              <th style={{ color: "#b71c1c" }}>Mode of Payment</th>
              <th style={{ color: "#0288d1" }}>Payment Method</th>
              {MONTHS.map(m => <th key={m} style={{ color: "#1976d2" }}>{m}</th>)}
              <th style={{ color: "#388e3c" }}>Misc Fee</th>
              <th style={{ color: "#0288d1" }}>Paid Months</th>
              <th style={{ color: "#0288d1" }}>Pending Months</th>
              <th style={{ color: "#0288d1" }}>Total Paid</th>
              <th style={{ color: "#b71c1c" }}>Balance</th>
              <th style={{ color: "#b71c1c" }}>Total Tuition</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className={`mode-row mode-${String(r.mode || "").toLowerCase()}`}>
                <td>{r.name}</td>
                <td>{r.gender}</td>
                <td>{r.section}</td>
                <td>{r.mode}</td>
                <td>
                  {r.latestMethod || ""}
                  {r.methodsUsed && r.methodsUsed.split(",").length > 1 && (
                    <div className="text-muted small">{r.methodsUsed}</div>
                  )}
                </td>
                {/* monthly-only view: no Annual / Semestral summary columns */}
                {MONTHS.map(m => {
                  const paid = Number(r.monthsTuition?.[m] || 0);
                  const dueAmt = (r.mode === "Installment" || r.mode === "Semestral") ? Number(r.dueMonthly?.[m] || 0) : 0;
                  const remain = Math.max(0, dueAmt - paid);
                  const ors = Array.isArray(r.tuitionOrsByMonth?.[m]) ? r.tuitionOrsByMonth[m].filter(Boolean) : [];
                  return (
                    <td key={m} style={{background: r.mode === "Installment" ? "rgba(25,118,210,0.06)" : undefined}}>
                      {paid > 0 ? `₱${paid.toLocaleString()}` : ""}
                      {ors.length > 0 && <div className="text-muted small">OR: {ors.join(", ")}</div>}
                      {remain > 0 && <div className="text-muted small">₱{remain.toLocaleString()} due</div>}
                    </td>
                  );
                })}
                <td style={{background: r.mode !== "Installment" ? "rgba(56,142,60,0.06)" : undefined}}>
                  {(() => {
                    const paidM = Number(r.misc1Paid || 0);
                    const dueM = Number(r.misc1Due || 0);
                    const remainM = Math.max(0, dueM - paidM);
                    if (paidM > 0) return `₱${paidM.toLocaleString()}`;
                    if (remainM > 0) return <span className="text-muted small">₱{remainM.toLocaleString()} due</span>;
                    return "";
                  })()}
                  {Array.isArray(r.miscOrs) && r.miscOrs.filter(Boolean).length > 0 && (
                    <div className="text-muted small">OR: {r.miscOrs.filter(Boolean).join(", ")}</div>
                  )}
                </td>
                <td>{Number(r.paidMonthsCount || 0)}</td>
                <td>{Math.max(0, Number(r.requiredMonths || MONTHS.length) - Number(r.paidMonthsCount || 0))}</td>
                <td>₱{Number(r.totalPaid || 0).toLocaleString()}</td>
                <td>₱{Number(r.balance || 0).toLocaleString()}</td>
                <td>₱{Number(r.totalTuition || 0).toLocaleString()}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
              <td colSpan={7 + MONTHS.length + 5} className="text-center text-muted">No students match the current filters</td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr className="fw-bold bg-light">
              <td colSpan={18} className="text-end">Totals</td>
              <td className="text-end">₱{rows.reduce((sum, r) => sum + Number(r.totalPaid || 0), 0).toLocaleString()}</td>
              <td className="text-end">₱{rows.reduce((sum, r) => sum + Number(r.balance || 0), 0).toLocaleString()}</td>
              <td className="text-end">₱{rows.reduce((sum, r) => sum + Number(r.totalTuition || 0), 0).toLocaleString()}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
